import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MapPin, ArrowRight, Compass, Search, Share2 } from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
  EmailIcon,
} from 'react-share';

const ThingsToDoLandingPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const distinctCounties = useMemo(() => {
    return [
      'Antrim',
      'Armagh',
      'Carlow',
      'Cavan',
      'Clare',
      'Cork',
      'Derry',
      'Donegal',
      'Down',
      'Dublin',
      'Fermanagh',
      'Galway',
      'Kerry',
      'Kildare',
      'Kilkenny',
      'Laois',
      'Leitrim',
      'Limerick',
      'Longford',
      'Louth',
      'Mayo',
      'Meath',
      'Monaghan',
      'Offaly',
      'Roscommon',
      'Sligo',
      'Tipperary',
      'Tyrone',
      'Waterford',
      'Westmeath',
      'Wexford',
      'Wicklow',
    ];
  }, []);

  const filteredCounties = distinctCounties.filter((county) =>
    county.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>Things to Do in Ireland 2026 - Ultimate County Travel Guide</title>
        <meta
          name="description"
          content="Explore the ultimate guide to things to do in Ireland. Discover local hidden gems, top tourist attractions, family activities, and cultural experiences across all 32 counties. Plan your perfect Irish adventure today."
        />
        <meta
          name="keywords"
          content="things to do in Ireland, Ireland travel guide, visit Ireland, Irish tourism, things to do in Dublin, things to do in Cork, things to do in Galway, family activities Ireland, hidden gems Ireland, best places to visit in Ireland, Ireland sightseeing, county guides Ireland, wild atlantic way, ancient east, northern ireland tourism"
        />
        <meta
          property="og:title"
          content="Things to Do in Ireland 2026 - Ultimate County Travel Guide"
        />
        <meta
          property="og:description"
          content="Discover local hidden gems, top tourist attractions, and cultural experiences across all 32 counties of Ireland."
        />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://rentsmart.ie/things-to-do" />
      </Helmet>

      <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-visible min-h-screen bg-cover bg-center bg-no-repeat font-sans">
        {/* HERO SECTION */}
        <div className="relative bg-[#1B365D] text-white py-20 px-4 sm:px-6 overflow-hidden">
          {/* Abstract Background Effects */}
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-blue-500/20 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 rounded-full bg-indigo-500/20 blur-3xl"></div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-blue-200 text-xs font-bold uppercase tracking-widest mb-6 backdrop-blur-sm border border-white/10">
              <Compass className="h-4 w-4" /> Ireland Travel Guide
            </div>
            <h1 className="text-4xl sm:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
              Discover the{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
                Real Ireland
              </span>
            </h1>
            <p className="text-lg sm:text-2xl text-blue-100 max-w-2xl mx-auto mb-10 font-light leading-relaxed">
              Explore curated guides, hidden gems, and local secrets for every county in the Emerald
              Isle.
            </p>

            <div className="max-w-xl mx-auto relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                placeholder="Find your county..."
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-blue-200/70 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white/20 transition-all shadow-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Social Share */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <span className="flex items-center gap-2 text-blue-200 text-sm font-medium">
                <Share2 className="h-4 w-4" /> Share Guide:
              </span>
              <div className="flex gap-3">
                <FacebookShareButton url={'https://rentsmart.ie/things-to-do'}>
                  <FacebookIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </FacebookShareButton>
                <TwitterShareButton
                  url={'https://rentsmart.ie/things-to-do'}
                  title={'Discover the best things to do in every county of Ireland!'}
                >
                  <TwitterIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </TwitterShareButton>
                <WhatsappShareButton
                  url={'https://rentsmart.ie/things-to-do'}
                  title={'Check out this guide to Ireland!'}
                >
                  <WhatsappIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </WhatsappShareButton>
                <LinkedinShareButton
                  url={'https://rentsmart.ie/things-to-do'}
                  title={'Things to Do in Ireland 2026 - Ultimate County Travel Guide'}
                >
                  <LinkedinIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </LinkedinShareButton>
                <EmailShareButton
                  url={'https://rentsmart.ie/things-to-do'}
                  subject={'Things to Do in Ireland Guide'}
                  body={'Check out this ultimate guide to things to do in Ireland: '}
                >
                  <EmailIcon
                    size={32}
                    round
                    className="hover:opacity-90 hover:scale-110 transition-all shadow-lg"
                  />
                </EmailShareButton>
              </div>
            </div>
          </div>
        </div>

        {/* COUNTIES GRID */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          {filteredCounties.length === 0 ? (
            <div className="text-center py-20">
              <h3 className="text-xl text-slate-500">No counties found matching "{searchTerm}"</h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCounties.map((county) => (
                <Link
                  key={county}
                  to={`/things-to-do/${county.toLowerCase()}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col h-full hover:-translate-y-1"
                >
                  {/* Decorative Card Header - SVG Pattern */}
                  <div
                    className="h-40 w-full relative overflow-hidden transition-colors duration-300"
                    style={{ backgroundColor: getCountyColors(county).bg }}
                  >
                    <svg
                      className="absolute inset-0 w-full h-full opacity-60"
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="none"
                      viewBox="0 0 400 160"
                    >
                      <path
                        fill={getCountyColors(county).primary}
                        fillOpacity="0.15"
                        d="M0,128L48,117.3C96,107,192,85,288,80C384,75,480,85,576,90.7C672,96,768,96,864,85.3C960,75,1056,53,1152,42.7C1248,32,1344,32,1392,32L1440,32L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
                      />
                      <circle
                        cx="360"
                        cy="20"
                        r="80"
                        fill={getCountyColors(county).secondary}
                        fillOpacity="0.05"
                      />
                      <circle
                        cx="20"
                        cy="140"
                        r="60"
                        fill={getCountyColors(county).secondary}
                        fillOpacity="0.05"
                      />
                    </svg>

                    <div className="absolute bottom-4 left-4 flex items-center gap-2">
                      <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-100/50">
                        <MapPin
                          className="h-5 w-5"
                          style={{ color: getCountyColors(county).primary }}
                        />
                      </div>
                      <span className="font-bold text-lg tracking-wide text-slate-800">
                        {county}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 flex-grow flex flex-col justify-between no-underline">
                    <div>
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2 no-underline">
                        Discover the best attractions, food, and seasonal events in Co. {county}.
                      </p>
                    </div>
                    <div className="flex items-center text-blue-600 font-bold text-sm mt-auto no-underline">
                      Explore Guide{' '}
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Helper to generate deterministic but varied colors based on county name
const getCountyColors = (name: string) => {
  const colors = [
    { bg: '#eff6ff', primary: '#3b82f6', secondary: '#2563eb' }, // Blue
    { bg: '#f0fdf4', primary: '#22c55e', secondary: '#16a34a' }, // Green
    { bg: '#faf5ff', primary: '#a855f7', secondary: '#7e22ce' }, // Purple
    { bg: '#fff7ed', primary: '#f97316', secondary: '#ea580c' }, // Orange
    { bg: '#ecfeff', primary: '#06b6d4', secondary: '#0891b2' }, // Cyan
    { bg: '#fdf2f8', primary: '#ec4899', secondary: '#be185d' }, // Pink
  ];
  // Simple hash to pick a color theme
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export default ThingsToDoLandingPage;
